import React from 'react'
import { Box, Typography, Grid, Container, styled, Divider } from '@mui/material'
import { FormattedMessage } from 'react-intl'
import ColorPicker from '../../../components/ColorPicker'

interface StyleData {
  primaryColor: string
  secondaryColor: string
  accentColor: string
  backgroundColor: string
  textColor: string
  fontColor: string
}

interface CustomerStyleSectionProps {
  styleData: StyleData
  onStyleChange: (field: keyof StyleData, value: string) => void
  disabled?: boolean
}

const StyledContainer = styled(Container)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}))

const SectionHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 0),
  borderBottom: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(3),
}))

const ColorGroup = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}))

const GroupTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(2),
}))

const CustomerStyleSection: React.FC<CustomerStyleSectionProps> = ({
  styleData,
  onStyleChange,
  disabled = false,
}) => {
  const handleColorChange = (field: keyof StyleData) => (color: string) => {
    onStyleChange(field, color)
  }

  return (
    <StyledContainer maxWidth="md" className="styleSection subFormContainer">
      <SectionHeader>
        <Typography variant="h5" component="div" className="header">
          <FormattedMessage id="customer-style" defaultMessage="Customer Style" />
        </Typography>
      </SectionHeader>

      <Box className="content">
        <Grid container spacing={3}>
          {/* Brand Colors */}
          <Grid item xs={12}>
            <ColorGroup>
              <GroupTitle variant="h6">
                <FormattedMessage id="brand-colors" defaultMessage="Brand Colors" />
              </GroupTitle>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <ColorPicker
                    label="Primary Color"
                    color={styleData.primaryColor}
                    onChange={handleColorChange('primaryColor')}
                    disabled={disabled}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ColorPicker
                    label="Secondary Color"
                    color={styleData.secondaryColor}
                    onChange={handleColorChange('secondaryColor')}
                    disabled={disabled}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ColorPicker
                    label="Accent Color"
                    color={styleData.accentColor}
                    onChange={handleColorChange('accentColor')}
                    disabled={disabled}
                  />
                </Grid>
              </Grid>
            </ColorGroup>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>

          {/* Background & Text Colors */}
          <Grid item xs={12}>
            <ColorGroup>
              <GroupTitle variant="h6">
                <FormattedMessage
                  id="background-text-colors"
                  defaultMessage="Background & Text Colors"
                />
              </GroupTitle>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <ColorPicker
                    label="Background Color"
                    color={styleData.backgroundColor}
                    onChange={handleColorChange('backgroundColor')}
                    disabled={disabled}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ColorPicker
                    label="Text Color"
                    color={styleData.textColor}
                    onChange={handleColorChange('textColor')}
                    disabled={disabled}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ColorPicker
                    label="Font Color"
                    color={styleData.fontColor}
                    onChange={handleColorChange('fontColor')}
                    disabled={disabled}
                  />
                </Grid>
              </Grid>
            </ColorGroup>
          </Grid>

          {/* Preview Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <ColorGroup>
              <GroupTitle variant="h6">
                <FormattedMessage id="color-preview" defaultMessage="Preview" />
              </GroupTitle>
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: 1,
                  borderColor: 'divider',
                  backgroundColor: styleData.backgroundColor || 'background.paper',
                  color: styleData.textColor || 'text.primary',
                  transition: 'all 0.3s ease',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: styleData.primaryColor || 'primary.main',
                    fontWeight: 'bold',
                    mb: 1,
                  }}
                >
                  Sample Heading
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: styleData.fontColor || 'text.secondary',
                    mb: 2,
                  }}
                >
                  This is how your content will look with the selected colors.
                </Typography>
                <Box
                  sx={{
                    display: 'inline-block',
                    px: 2,
                    py: 1,
                    backgroundColor: styleData.secondaryColor || 'secondary.main',
                    color: '#fff',
                    borderRadius: 1,
                    mr: 1,
                  }}
                >
                  Secondary Button
                </Box>
                <Box
                  sx={{
                    display: 'inline-block',
                    px: 2,
                    py: 1,
                    backgroundColor: styleData.accentColor || 'warning.main',
                    color: '#fff',
                    borderRadius: 1,
                  }}
                >
                  Accent Button
                </Box>
              </Box>
            </ColorGroup>
          </Grid>
        </Grid>
      </Box>
    </StyledContainer>
  )
}

export default CustomerStyleSection
export type { StyleData }
